import { MouseEvent, MutableRefObject, useCallback, useMemo, useRef, useState } from 'react'

import { ReactCallBackFunction } from 'src/types/CallbackFunction'
import Direction from 'src/types/DirectionInterface'

const clearTimeoutRef = (ref: MutableRefObject<number | NodeJS.Timeout | undefined>) => {
  if (ref.current) {
    clearTimeout(ref.current as number)
  }
}

interface HoverProps<T> {
  onMouseMove?: (evt: MouseEvent<T>) => void
  onMouseLeave?: (evt: MouseEvent<T>) => void
  isMouseEnter?: boolean // Use mouse over or mouse move event
  mouseLeaveDelay?: number // Delay before the mouse leave event is triggered
  disableMouseLeave?: boolean
  mouseEnterDelay?: number // Delay before the mouse enter event is triggered
  mouseLeaveActiveDirection?: Direction // If exists, the mouse leave delay will be applied in function of the velocity and direction of the mouse
}

const useHover = <T = HTMLDivElement>(args?: HoverProps<T>) => {
  const [isHovered, setIsHovered] = useState(false)
  const [isDelayedHover, setIsDelayedHover] = useState(false) // On unhover, this state is true until mouseLeaveDelay is over

  const timeoutMouseLeaveRef = useRef<NodeJS.Timeout | number>()
  const timeoutMouseEnterRef = useRef<NodeJS.Timeout | number>()
  const timeoutMouseMoveDisabledRef = useRef<NodeJS.Timeout | number>()
  const isMouseEnteredRef = useRef(false)
  const isMouseMoveDisabledRef = useRef(false)

  // Add refs for tracking mouse position only for mouse leave velocity
  const lastMousePositionRef = useRef<{ x: number; y: number; timestamp: number } | null>(null)

  const handleSetIsHovered: ReactCallBackFunction<boolean> = useCallback(
    (state: boolean | ((prevState: boolean) => boolean)) => {
      clearTimeoutRef(timeoutMouseLeaveRef)
      setIsHovered(s => (typeof state === 'function' ? state(s) : state))
      setIsDelayedHover(s => (typeof state === 'function' ? state(s) : state))
    },
    [],
  )

  const onHoverLeave = useCallback(() => {
    clearTimeoutRef(timeoutMouseLeaveRef)
    clearTimeoutRef(timeoutMouseEnterRef)
    isMouseEnteredRef.current = false
    setIsHovered(false)
    setIsDelayedHover(false)
    isMouseMoveDisabledRef.current = true

    // Re-enable mouse move after 100ms
    timeoutMouseMoveDisabledRef.current = setTimeout(() => {
      isMouseMoveDisabledRef.current = false
    }, 100)
  }, [])

  const onMouseMove = useCallback(
    (evt: MouseEvent<T>) => {
      // Return early if mouse move is disabled
      if (isMouseMoveDisabledRef.current) {
        return
      }

      setIsHovered(true)

      // Only track last mouse position for velocity calculation on leave
      const currentTime = Date.now()
      const currentPos = { x: evt.clientX, y: evt.clientY }
      lastMousePositionRef.current = { ...currentPos, timestamp: currentTime }

      const handleMouseMove = () => {
        setIsDelayedHover(true)
        args?.onMouseMove?.(evt)
      }

      if (timeoutMouseLeaveRef.current) {
        clearTimeout(timeoutMouseLeaveRef.current as number)
      }
      if (args?.mouseEnterDelay) {
        if (!isMouseEnteredRef.current) {
          isMouseEnteredRef.current = true
          if (timeoutMouseEnterRef.current) {
            clearTimeout(timeoutMouseEnterRef.current as number)
          }
          timeoutMouseEnterRef.current = setTimeout(handleMouseMove, args.mouseEnterDelay)
        }
      } else {
        handleMouseMove()
      }
    },
    [args?.onMouseMove, args?.mouseEnterDelay, isHovered, args?.mouseLeaveActiveDirection],
  )

  const onMouseLeave = useCallback(
    (evt: MouseEvent<T>) => {
      // clear mouse enter timeout
      isMouseEnteredRef.current = false
      if (timeoutMouseEnterRef.current) {
        clearTimeout(timeoutMouseEnterRef.current as number)
        timeoutMouseEnterRef.current = undefined
      }

      const handleLeave = () => {
        handleSetIsHovered(false)
        args?.onMouseLeave?.(evt)
      }

      // Calculate effective delay based on velocity and direction at mouse leave
      let effectiveDelay = args?.mouseLeaveDelay || 0

      if (
        args?.mouseLeaveActiveDirection &&
        args?.mouseLeaveDelay &&
        lastMousePositionRef.current
      ) {
        const targetDirection = args.mouseLeaveActiveDirection

        // Calculate velocity at mouse leave
        const currentTime = Date.now()
        const currentPos = { x: evt.clientX, y: evt.clientY }
        const deltaTime = currentTime - lastMousePositionRef.current.timestamp
        const deltaX = currentPos.x - lastMousePositionRef.current.x
        const deltaY = currentPos.y - lastMousePositionRef.current.y

        if (deltaTime > 0) {
          // Determine primary direction based on largest movement
          const d: Direction[] = []

          if (deltaX !== 0) {
            d.push(deltaX > 0 ? 'right' : 'left')
          }

          if (deltaY !== 0) {
            d.push(deltaY > 0 ? 'down' : 'up')
          }

          // Only apply delay if movement matches specified direction and meets velocity threshold
          if (d.includes(targetDirection)) {
            effectiveDelay = args.mouseLeaveDelay
          } else {
            effectiveDelay = 0
          }
        } else {
          effectiveDelay = 0
        }
      }

      if (effectiveDelay > 0) {
        setIsHovered(false)
        timeoutMouseLeaveRef.current = setTimeout(handleLeave, effectiveDelay)
      } else {
        handleLeave()
      }
    },
    [
      args?.onMouseLeave,
      args?.mouseLeaveDelay,
      args?.mouseLeaveActiveDirection,
      handleSetIsHovered,
    ],
  )

  const hoverProps = useMemo(
    () => ({
      onMouseMove: !args?.isMouseEnter ? onMouseMove : undefined,
      onMouseLeave: args?.disableMouseLeave ? undefined : onMouseLeave,
      onMouseEnter: args?.isMouseEnter ? onMouseMove : undefined,
    }),
    [onMouseMove, onMouseLeave, args?.isMouseEnter, args?.disableMouseLeave],
  )

  return {
    isHovered,
    delayedHover: isDelayedHover,
    setIsHovered: handleSetIsHovered,
    hoverProps,
    leaveHover: onHoverLeave,
  }
}

export default useHover
